/* Diktieren in jedes Textfeld des angemeldeten CRM (30.10.2026, „wie Wispr Flow“). Einmal in AppLayout eingehängt.
   Mac/Desktop: ⌥ (Option) allein gedrückt halten (ab 250 ms) = aufnehmen, loslassen = Text einfügen, Esc = abbrechen.
   Handy/Tablet: solange ein Textfeld Fokus hat, schwebt ein Mikro-Knopf; gedrückt halten = aufnehmen, loslassen = einfügen, wegwischen = abbrechen.
   Erkennung NUR über den eigenen Server (POST /api/diktat → eigener Whisper, feste Aufräum-Regeln, keine KI). Das Audio wird nicht gespeichert. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { useSprachaufnahme, zeit } from '@/lib/sprachaufnahme';
import { einfuegePlan, feldArt, zielFeld } from '@/lib/diktatFeld';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SCHLUESSEL = 'p1_diktat';
const EREIGNIS = 'p1-diktat-einstellung';
const HALTEN_MS = 250;
const WEGWISCHEN_PX = 90;
export const TASTEN = { alt: { key: 'Alt', name: 'Option ⌥' }, control: { key: 'Control', name: 'Control ⌃' } };

export function diktatEinstellung() {
  try {
    const e = JSON.parse(localStorage.getItem(SCHLUESSEL) || '{}');
    return { an: e.an !== false, taste: TASTEN[e.taste] ? e.taste : 'alt' };
  } catch (err) { return { an: true, taste: 'alt' }; }
}
export function diktatEinstellungSchreiben(neu) {
  try { localStorage.setItem(SCHLUESSEL, JSON.stringify(neu)); } catch (err) { /* ohne Speicher: gilt nur bis zum Neuladen */ }
  window.dispatchEvent(new Event(EREIGNIS));
}
function useEinstellung() {
  const [e, setE] = useState(diktatEinstellung);
  useEffect(() => {
    const neu = () => setE(diktatEinstellung());
    window.addEventListener(EREIGNIS, neu);
    return () => window.removeEventListener(EREIGNIS, neu);
  }, []);
  return e;
}

function merkeAuswahl(el) {
  if (el.isContentEditable) {
    const s = window.getSelection();
    return s && s.rangeCount && el.contains(s.anchorNode) ? { bereich: s.getRangeAt(0).cloneRange() } : { bereich: null };
  }
  try { return { start: el.selectionStart, ende: el.selectionEnd }; } catch (e) { return { start: null, ende: null }; }
}

export function einfuegen(el, text, auswahl) {
  if (!el || !el.isConnected) return false;
  if (el.isContentEditable) {
    el.focus({ preventScroll: true });
    const sel = window.getSelection();
    if (auswahl?.bereich && sel) { sel.removeAllRanges(); sel.addRange(auswahl.bereich); }
    const b = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    const k = b?.startContainer;
    const davor = k && k.nodeType === 3 && b.startOffset > 0 ? k.data[b.startOffset - 1] : '';
    const t = (text || '').trim();
    if (!t) return false;
    const vorn = davor && !/[\s(„"'[]/.test(davor) && !/^[.,;:!?)]/.test(t) ? ' ' : '';
    return document.execCommand('insertText', false, vorn + t);
  }
  const plan = einfuegePlan(el.value || '', auswahl?.start, auswahl?.ende, text);
  if (!plan) return false;
  let wert = plan.wert;
  let cursor = plan.cursor;
  if (el.maxLength > 0 && wert.length > el.maxLength) { wert = wert.slice(0, el.maxLength); cursor = Math.min(cursor, wert.length); }
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, wert);          // React-kontrolliert: nativer Setter + input-Ereignis
  el.dispatchEvent(new Event('input', { bubbles: true }));
  try { el.focus({ preventScroll: true }); el.setSelectionRange(cursor, cursor); } catch (e) { /* Feldtyp ohne Cursor */ }
  return true;
}

/* ── Baustein ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────── */
const berührt = () => typeof window !== 'undefined' && (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window);

export default function Diktat() {
  const einst = useEinstellung();
  const ziel = useRef(null);                       // Feld und Auswahl zum Zeitpunkt des Starts
  const [touch] = useState(berührt);
  const [fokusFeld, setFokusFeld] = useState(null);
  const [unten, setUnten] = useState(0);            // Abstand über der Bildschirmtastatur
  const [weg, setWeg] = useState(false);            // Finger weit genug weggewischt = Abbruch
  const wartet = useRef(null);                      // Zeitgeber „Taste gehalten“
  const gehalten = useRef(false);                   // Taste/Finger ist gerade unten
  const abgebrochen = useRef(false);

  const eingefuegt = useCallback((text) => {
    const z = ziel.current;
    ziel.current = null;
    if (!z) return;
    if (!einfuegen(z.el, text, z.auswahl)) toast.error('Der Text konnte nicht eingefügt werden. Das Feld ist nicht mehr da.');
  }, []);
  // die Art des Feldes geht mit der Aufnahme an den Server; der Hook ruft `felder` erst beim Senden auf
  const s = useSprachaufnahme({ onText: eingefuegt, pfad: '/diktat', felder: () => ({ feld: ziel.current?.art }), dateiname: 'diktat' });
  const sRef = useRef(s);
  sRef.current = s;

  useEffect(() => {
    if (s.fehler) { toast.error(s.fehler); s.fehlerWeg(); }
  }, [s.fehler]);                                  // eslint-disable-line react-hooks/exhaustive-deps

  const starten = useCallback(async () => {
    const el = zielFeld(document.activeElement) || zielFeld(fokusFeld);
    if (!el) return false;
    abgebrochen.current = false;
    ziel.current = { el, auswahl: merkeAuswahl(el), art: feldArt(el) };
    await sRef.current.start();
    if (abgebrochen.current) { sRef.current.abbrechen(); ziel.current = null; return false; }      // vor der Freigabe des Mikrofons losgelassen
    if (!gehalten.current && sRef.current.laeuft()) sRef.current.fertig();
    return true;
  }, [fokusFeld]);

  const beenden = useCallback((senden) => {
    gehalten.current = false;
    clearTimeout(wartet.current);
    wartet.current = null;
    abgebrochen.current = !senden;
    if (sRef.current.laeuft()) {
      if (senden) sRef.current.fertig(); else { sRef.current.abbrechen(); ziel.current = null; }
    } else if (!senden) ziel.current = null;
  }, []);

  /* Fokus verfolgen (für den Mikro-Knopf am Handy) */
  useEffect(() => {
    if (!touch) return undefined;
    const rein = (e) => setFokusFeld(zielFeld(e.target));
    const raus = () => setTimeout(() => { if (!zielFeld(document.activeElement)) setFokusFeld(null); }, 150);
    document.addEventListener('focusin', rein);
    document.addEventListener('focusout', raus);
    setFokusFeld(zielFeld(document.activeElement));
    return () => { document.removeEventListener('focusin', rein); document.removeEventListener('focusout', raus); };
  }, [touch]);

  /* Abstand über der Tastatur */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const rechne = () => setUnten(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    rechne();
    vv.addEventListener('resize', rechne);
    vv.addEventListener('scroll', rechne);
    return () => { vv.removeEventListener('resize', rechne); vv.removeEventListener('scroll', rechne); };
  }, []);

  /* Tastatur: nur die Taste allein, ≥ 250 ms. Jede weitere Taste bricht ab, Esc auch. */
  useEffect(() => {
    if (!einst.an) return undefined;
    const taste = TASTEN[einst.taste].key;
    const runter = (e) => {
      if (e.key === taste) {
        if (e.repeat || gehalten.current) return;
        if (e.metaKey || e.shiftKey || (taste === 'Alt' ? e.ctrlKey : e.altKey)) return;
        if (!zielFeld(document.activeElement)) return;
        gehalten.current = true;
        wartet.current = setTimeout(() => {
          wartet.current = null;
          if (gehalten.current && zielFeld(document.activeElement)) starten();
        }, HALTEN_MS);
        return;
      }
      if (e.key === 'Escape' && (sRef.current.zustand === 'aufnahme' || wartet.current)) {
        e.preventDefault(); e.stopPropagation(); beenden(false); return;
      }
      if (gehalten.current) beenden(false);                       // Tastenkombination (⌥ + Buchstabe …): kein Diktat
    };
    const hoch = (e) => {
      if (e.key !== taste || !gehalten.current) return;
      const lief = sRef.current.zustand === 'aufnahme' || !!sRef.current.laeuft();
      if (!lief) { gehalten.current = false; clearTimeout(wartet.current); wartet.current = null; return; }   // nur angetippt
      beenden(true);
    };
    const verloren = () => { if (gehalten.current) beenden(false); };
    window.addEventListener('keydown', runter, true);
    window.addEventListener('keyup', hoch, true);
    window.addEventListener('blur', verloren);
    return () => {
      window.removeEventListener('keydown', runter, true);
      window.removeEventListener('keyup', hoch, true);
      window.removeEventListener('blur', verloren);
      clearTimeout(wartet.current);
    };
  }, [einst.an, einst.taste, starten, beenden]);

  /* Mikro-Knopf (Finger oder Maus) */
  const start0 = useRef({ x: 0, y: 0 });
  const knopfRunter = (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* egal */ }
    start0.current = { x: e.clientX, y: e.clientY };
    gehalten.current = true; setWeg(false);
    starten();
  };
  const knopfBewegt = (e) => {
    if (!gehalten.current) return;
    setWeg(Math.hypot(e.clientX - start0.current.x, e.clientY - start0.current.y) > WEGWISCHEN_PX);
  };
  const knopfHoch = (e) => {
    if (!gehalten.current) return;
    const wischen = Math.hypot(e.clientX - start0.current.x, e.clientY - start0.current.y) > WEGWISCHEN_PX;
    setWeg(false);
    beenden(!wischen);
  };
  const knopfAbbruch = () => { setWeg(false); beenden(false); };

  if (!einst.an) return null;
  const aktiv = s.zustand !== 'ruhig';
  const zeigeKnopf = touch && (fokusFeld || aktiv);
  const basis = `calc(${unten}px + env(safe-area-inset-bottom, 0px)`;
  return (
    <>
      {aktiv && (
        <div role="status" aria-live="polite" data-testid="diktat-band" data-print="rahmen"
          className="fixed left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 rounded-full border border-border bg-card/95 px-4 py-2.5 text-sm shadow-lg backdrop-blur"
          style={{ bottom: `${basis} + ${touch ? 84 : 24}px)`, pointerEvents: 'none' }}>
          {s.zustand === 'erkennen' ? (
            <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /><span>Wird geschrieben …</span></>
          ) : weg ? (
            <span>Loslassen zum Abbrechen</span>
          ) : (
            <>
              <span className="h-2 w-2 rounded-full bg-marke motion-safe:animate-pulse" aria-hidden="true" />
              <span className="whitespace-nowrap tabular-nums">Ich höre zu … {zeit(s.sek)}</span>
              <span className="h-2 w-16 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                <span className="block h-full bg-marke transition-[width] duration-75" style={{ width: `${Math.round(s.pegel * 100)}%` }} />
              </span>
              <span className="hidden text-xs text-muted-foreground sm:inline">{touch ? 'Wegwischen = abbrechen' : 'Esc = abbrechen'}</span>
            </>
          )}
        </div>
      )}
      {zeigeKnopf && (
        <button type="button" aria-label="Diktieren: gedrückt halten und sprechen, loslassen zum Einfügen" title="Gedrückt halten und sprechen"
          data-testid="diktat-knopf" data-print="rahmen"
          onPointerDown={knopfRunter} onPointerMove={knopfBewegt} onPointerUp={knopfHoch} onPointerCancel={knopfAbbruch}
          onMouseDown={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}
          disabled={s.zustand === 'erkennen'}
          className={`fixed right-4 z-[100] flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-marke focus:ring-offset-2 ${weg ? 'bg-muted-foreground' : 'bg-marke'} ${s.zustand === 'aufnahme' ? 'motion-safe:scale-110' : ''} motion-safe:transition-transform disabled:opacity-60`}
          style={{ bottom: `${basis} + 16px)`, touchAction: 'none', WebkitTouchCallout: 'none', userSelect: 'none', pointerEvents: 'auto' }}>
          {s.zustand === 'erkennen' ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Mic className="h-5 w-5" aria-hidden="true" />}
        </button>
      )}
    </>
  );
}

/* ── Einstellung je Gerät (Seitenleiste, neben Dunkelmodus) ──────────────────────────────────────────────────────────────────── */
export function DiktatEintrag({ eingeklappt = false }) {
  const [offen, setOffen] = useState(false);
  const e = useEinstellung();
  const beschriftung = e.an ? `Diktieren: ${touch0() ? 'an' : TASTEN[e.taste].name}` : 'Diktieren: aus';
  return (
    <>
      <button type="button" onClick={() => setOffen(true)} data-testid="diktat-einstellung" aria-haspopup="dialog" title="Diktieren einstellen"
        className={eingeklappt
          ? 'hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors'
          : 'flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors'}>
        <Mic className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true" />
        {!eingeklappt && <span>{beschriftung}</span>}
        {eingeklappt && <span className="sr-only">{beschriftung}</span>}
      </button>
      <Dialog open={offen} onOpenChange={setOffen}>
        <DialogContent className="max-w-md" data-diktat="aus">
          <DialogHeader>
            <DialogTitle>Diktieren</DialogTitle>
            <DialogDescription>Sprechen statt tippen – in jedem Textfeld des CRM. Die Aufnahme bleibt auf unserem Server und wird nicht gespeichert.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="flex gap-2" role="group" aria-label="Diktieren an oder aus">
              {[[true, 'An'], [false, 'Aus']].map(([wert, name]) => (
                <button key={name} type="button" aria-pressed={e.an === wert} onClick={() => diktatEinstellungSchreiben({ ...e, an: wert })}
                  className={`min-h-[44px] flex-1 rounded-md border px-3 ${e.an === wert ? 'border-marke bg-marke/10 font-medium' : 'border-input hover:bg-muted'}`}>{name}</button>
              ))}
            </div>
            <div>
              <div className="mb-1.5 font-medium">Taste am Computer</div>
              <div className="flex gap-2" role="group" aria-label="Taste zum Diktieren">
                {Object.entries(TASTEN).map(([id, t]) => (
                  <button key={id} type="button" aria-pressed={e.taste === id} disabled={!e.an} onClick={() => diktatEinstellungSchreiben({ ...e, taste: id })}
                    className={`min-h-[44px] flex-1 rounded-md border px-3 disabled:opacity-50 ${e.taste === id ? 'border-marke bg-marke/10 font-medium' : 'border-input hover:bg-muted'}`}>{t.name}</button>
                ))}
              </div>
            </div>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Cursor in ein Textfeld setzen, die Taste allein <strong>gedrückt halten</strong> (ab einer Viertelsekunde), sprechen, loslassen.</li>
              <li>Ein kurzes Antippen der Taste bleibt für Sonderzeichen frei; mit einer weiteren Taste wird nicht aufgenommen. Esc bricht ab.</li>
              <li>Am Handy: Textfeld antippen, dann den runden Knopf gedrückt halten. Wegwischen bricht ab.</li>
              <li>Sag „Punkt“, „Komma“, „Fragezeichen“ oder „neue Zeile“, um Satzzeichen zu setzen.</li>
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
function touch0() { return berührt() && !window.matchMedia?.('(pointer: fine)').matches; }
